/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SalesLagReport } from '../models/reports/sales-lag-report.model';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class ManageSalesLagService {
  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Gets all sales lag report data
   * @return the list of sales lag report elements
   */
  getSalesLagReport(): Observable<SalesLagReport[]> {
    return this.http.get<SalesLagReport[]>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/sales-lag-report`).pipe(
      map((res) => res), catchError(myError => this.handleError(myError)));
  }

  /**
   * updates a list of brandfam object's eos gap and sos gap
   * @param salesLagReportList List of Sale Lag Report data
   * @return Observable<void>
   */
  updateSalesLagReport(salesLagReportList: SalesLagReport[]): Observable<void> {
    return this.http.put<void>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/sales-lag-report`, salesLagReportList).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * Transforms the sales Lag Report data the user uploads to an object to represent the data
   * @param file the sales lag report data file that is uploaded
   * @param sheetList a list containing the selected sheets to read data from
   */
  transform(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(`${this.configService.baseApiUrl}manage-gfs-vehicles/brandfam/sales-lag-report/transform`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
